import { Injectable, inject } from '@angular/core';
import { Observable, catchError, from, map, of, switchMap } from 'rxjs';
import { NetworkService } from '../../services/network.service';
import { SignService } from '../../services/sign.service';
import { UpqueueService } from '../../services/upqueue.service';
import { DeviceCryptoService } from '../../services/device-crypto.service';
import { ToastService } from '../../../shared/services/toast.service';
import { UpqueueAdapter } from './upqueue.adapter';
import { encodeEncryptedDocument } from './adapter.utils';
import type { AffidavitRecord } from '../../models/signature.model';

// ─── Types ────────────────────────────────────────────────────────────────────

/**
 * GPS input accepted by `submit()` and `toPayload()`.
 *
 * @remarks
 * Mapped to the `location` object in the wire payload
 * (`latitude`, `longitude`, `accuracy_m`, `captured_at`).
 * Also forwarded to `SignService.markSigned()` on confirmed online success
 * to populate `signedCoords` on the signed card in the Sign Queue.
 */
export interface AffidavitLocationInput {
  latitude: number;
  longitude: number;
  /** GPS fix accuracy in metres. */
  accuracy?: number;
  /** Unix timestamp (ms) when the GPS fix was captured. */
  timestamp?: number;
}

/** `location` object nested inside {@link AffidavitPayload}. */
export interface AffidavitLocationPayload {
  latitude:    number;
  longitude:   number;
  accuracy_m?: number;
  captured_at?: number;
}

/** Wire payload sent to `POST /process-server/{id}/jobs/esignqueue`. */
export interface AffidavitPayload {
  signed_affidavits: AffidavitPayloadItem[];
  /** GPS coordinates nested under `location`. Present when coordinates were available at submit time. */
  location?: AffidavitLocationPayload;
}

/** A single signed (or rejected) affidavit item in the wire payload. */
export interface AffidavitPayloadItem {
  job_number: string;
  affidavit_sign_status: string;
  affidavit_sign_key?: string;
  affidavit_sign_iv?: string;
  affidavit_sign_platform?: string;
  affidavit_sign_document?: string;
  /** `'html'` | `'pdf'` — backend validates `in:html,pdf` and stores to `esign_approved_jobs.document_format` (defaults to `'html'` when omitted). */
  affidavit_sign_document_format?: string;
  affidavit_sign_datetime?: string;
  affidavit_rejected_comments?: string;
  affidavit_rejected_datetime?: string;
}

/** Maximum data-URL length before JPEG compression is applied. */
const COMPRESS_THRESHOLD = 200_000;
/** JPEG quality used when compressing an oversized signature image. */
const COMPRESS_QUALITY = 0.7;

// ─── Adapter ──────────────────────────────────────────────────────────────────

/**
 * Adapter for the affidavit signing submission flow.
 *
 * @remarks
 * `submit()` assembles the wire payload, applies JPEG compression when the
 * signature image exceeds {@link COMPRESS_THRESHOLD} bytes, POSTs to
 * `POST /process-server/{serverId}/jobs/esignqueue`, and calls
 * `SignService.markSigned()` on success for an approval, or
 * `SignService.markRejectedByPS()` for a rejection.  On network failure (or
 * any HTTP error) it falls back to `UpqueueService.queue()` with an info
 * toast so the Observable always completes normally — the caller never
 * needs to handle errors.
 *
 * @example
 * ```ts
 * this.affidavitAdapter.submit(record, this.id, signedAt, gps).subscribe({
 *   complete: () => { void this.navCtrl.navigateBack('/sign'); }
 * });
 * ```
 */
@Injectable({ providedIn: 'root' })
export class AffidavitAdapter {
  private readonly networkService  = inject(NetworkService);
  private readonly signService     = inject(SignService);
  private readonly upqueueService  = inject(UpqueueService);
  private readonly upqueueAdapter  = inject(UpqueueAdapter);
  private readonly deviceCrypto    = inject(DeviceCryptoService);
  private readonly toastService    = inject(ToastService);

  // ─── Public API ─────────────────────────────────────────────────────────────

  /**
   * Builds the JSON-serialisable wire payload for the esignqueue endpoint.
   *
   * @remarks
   * When `gps` is provided the payload includes a `location` object with
   * `latitude`, `longitude`, and optional `accuracy_m` / `captured_at` fields.
   *
   * @param record  - Assembled affidavit record (encrypted document, keys, etc.).
   * @param gps     - Optional GPS coordinates to embed under `location`.
   * @returns       A fully JSON-serialisable {@link AffidavitPayload}.
   */
  toPayload(record: AffidavitRecord, gps?: AffidavitLocationInput | null): AffidavitPayload {
    const payload: AffidavitPayload = { signed_affidavits: [this.toPayloadItem(record)] };
    if (gps) {
      const location: AffidavitLocationPayload = { latitude: gps.latitude, longitude: gps.longitude };
      if (gps.accuracy   != null) location.accuracy_m  = gps.accuracy;
      if (gps.timestamp  != null) location.captured_at = gps.timestamp;
      payload.location = location;
    }
    return payload;
  }

  /**
   * Compresses a PNG signature data-URL to JPEG when it exceeds
   * {@link COMPRESS_THRESHOLD} characters.
   *
   * @remarks
   * Uses an off-screen canvas to re-encode at {@link COMPRESS_QUALITY} (0.7).
   * Returns the original data-URL unchanged when it is within the threshold.
   * Falls back to the original value if the canvas API is unavailable (e.g. SSR).
   *
   * @param dataUrl - Base-64 PNG data-URL from the signature canvas.
   * @returns       Compressed JPEG data-URL, or the original string if within threshold.
   */
  async compress(dataUrl: string): Promise<string> {
    if (dataUrl.length <= COMPRESS_THRESHOLD) return dataUrl;
    return new Promise<string>(resolve => {
      const img = new Image();
      img.onload = () => {
        try {
          const canvas    = document.createElement('canvas');
          canvas.width    = img.naturalWidth;
          canvas.height   = img.naturalHeight;
          const ctx       = canvas.getContext('2d');
          if (!ctx) { resolve(dataUrl); return; }
          ctx.drawImage(img, 0, 0);
          resolve(canvas.toDataURL('image/jpeg', COMPRESS_QUALITY));
        } catch {
          resolve(dataUrl);
        }
      };
      img.onerror = () => resolve(dataUrl);
      img.src     = dataUrl;
    });
  }

  /**
   * Submits a signed or rejected affidavit using a queue-first strategy.
   *
   * @remarks
   * Always enqueues the record before any network attempt — the submission is
   * never lost even if the device goes offline between enqueue and flush.
   * Immediately after enqueueing, branches on `record.signStatus`: a rejection
   * (`'R'`) calls `SignService.markRejectedByPS()` so the job shows under
   * "Waiting for TL review" right away; an approval (`'A'`) calls
   * `SignService.markQueuedForUpload()`, which moves the job out of "Ready to
   * sign" without the green "just signed" flag until the server confirms it.
   *
   * **Building the payload is now an async step (2026-08-05) — see
   * {@link buildQueuedPayload}.** `record.document` (already CRYPTOJSKEY-
   * encrypted for the server — see `AffidavitComponent.submitAffidavit()`) is
   * additionally encrypted at rest with a device-bound key before the item is
   * enqueued, because that first layer alone provides no protection against
   * someone reading `IDB_STORE_QUEUE` directly: the wrapped key needed to open
   * it (`signKey`/`signIv`) travels in the SAME queue entry, and `CRYPTOJSKEY`
   * itself is a plain string in the shipped bundle. See
   * `DeviceCryptoService`'s doc comment for the full reasoning.
   *
   * **This means `submit()` no longer enqueues synchronously on call** — the
   * `Observable` returned here is built via `from(buildQueuedPayload(...))`,
   * so the enqueue (and everything downstream: `markQueuedForUpload()` /
   * `markRejectedByPS()`, the online/offline branch) only happens once that
   * resolves. AES-GCM encryption of a single document-sized string is
   * sub-millisecond on real hardware (and the one-time device-key generation
   * on first-ever use is similarly fast), so in practice this adds a
   * negligible delay — but it is no longer decisively instantaneous within the
   * same synchronous tick as the `submit()` call, which callers relying on
   * exact timing (tests included) must account for.
   *
   * **Online path:** calls `UpqueueAdapter.transmitAll()` immediately after
   * enqueueing. `transmitAll()` POSTs to `/jobs/esignqueue` and, on confirmed
   * success, calls `SignService.markSigned()` for an approval or
   * `SignService.markRejectedByPS()` for a rejection (see
   * `UpqueueAdapter.transmitAffidavit()`, which decrypts the device-encrypted
   * document back to its original value before that POST). Success is
   * detected by checking whether the item left the queue — if it is gone the
   * Observable emits `false` (submitted), otherwise `true` (still queued,
   * will retry on next flush trigger).
   *
   * **Offline path:** shows an info toast and emits `true` (queued). The
   * item is flushed on the next reconnect event or SyncService P2 cycle.
   *
   * The Observable never errors — the caller can subscribe with a single
   * `next` handler and use the boolean to decide whether to show a success
   * toast.
   *
   * @param record   - Assembled affidavit record (see `toPayload()`). `record.signStatus`
   *                   (`'A'` | `'R'`) determines which SignService state transition fires.
   * @param jobId    - Job ID string used to update the Sign Queue on success.
   * @param signedAt - Submission timestamp stored in the queue entry.
   * @param gps      - Optional GPS coordinates embedded in the queued payload.
   * @returns        Observable that emits `false` when submitted online,
   *                 `true` when queued for later delivery.
   */
  submit(
    record: AffidavitRecord,
    jobId: string,
    signedAt: string,
    gps?: AffidavitLocationInput | null,
  ): Observable<boolean> {
    return from(this.buildQueuedPayload(record, gps)).pipe(
      switchMap(payload => {
        this.upqueueService.enqueue({
          jobId,
          type:      'affidavit',
          payload:   payload as unknown as Record<string, unknown>,
          timestamp: new Date().toISOString(),
          upqueued:  false,
        });
        // Move job out of "Ready to sign" immediately. Rejections go straight
        // to "Waiting for TL review"; approvals wait for markSigned() (called
        // by UpqueueAdapter.transmitAffidavit() when the POST succeeds) for
        // the green "just signed" flag.
        if (record.signStatus === 'R') {
          this.signService.markRejectedByPS(jobId);
        } else {
          this.signService.markQueuedForUpload(jobId);
        }

        if (!this.networkService.isOnline()) {
          void this.toastService.info('Affidavit saved to upload queue.');
          return of(true);
        }

        // Online: flush immediately and detect success by checking queue state.
        return this.upqueueAdapter.transmitAll({ quiet: true }).pipe(
          map(() => {
            const stillQueued = this.upqueueService.items().some(
              item => item.jobId === jobId && item.type === 'affidavit',
            );
            return stillQueued;
          }),
          catchError(() => of(true)), // transmitAll failed — item stays in queue for retry
        );
      }),
    );
  }

  // ─── Private helpers ────────────────────────────────────────────────────────

  /**
   * Builds the wire payload via {@link toPayload}, then additionally encrypts
   * `affidavit_sign_document` at rest with the device-bound key, when present.
   *
   * @remarks
   * A rejection record has no `document` (see `AffidavitRecord.document`'s own
   * doc comment — "a rejection record omits `document`/`signKey`/`signIv`"),
   * so this is a no-op for rejections; nothing to encrypt.
   *
   * If device encryption itself fails (IndexedDB or Web Crypto unavailable —
   * the same failure mode `DeviceCryptoService.encrypt()` can throw for), the
   * field is left as `toPayload()` produced it — the plain (device-
   * unencrypted) CRYPTOJSKEY-ciphertext string. That still reaches the server
   * correctly; only the local at-rest hardening is skipped for this one
   * submission. `UpqueueAdapter.transmitAffidavit()`'s
   * {@link decodeEncryptedDocument} correctly recognises this un-prefixed
   * shape and sends it through unchanged, so this fallback never corrupts the
   * submission — see that method's own doc comment.
   *
   * @param record - Assembled affidavit record.
   * @param gps    - Optional GPS coordinates to embed under `location`.
   */
  private async buildQueuedPayload(
    record: AffidavitRecord,
    gps?: AffidavitLocationInput | null,
  ): Promise<AffidavitPayload> {
    const payload = this.toPayload(record, gps);
    const item = payload.signed_affidavits[0];
    if (item.affidavit_sign_document) {
      try {
        const encrypted = await this.deviceCrypto.encrypt(item.affidavit_sign_document);
        item.affidavit_sign_document = encodeEncryptedDocument(encrypted);
      } catch {
        // Fall through with the unencrypted-at-device-level value — see this
        // method's own doc comment for why that's safe.
      }
    }
    return payload;
  }

  /** Maps an {@link AffidavitRecord} to its wire representation. */
  private toPayloadItem(record: AffidavitRecord): AffidavitPayloadItem {
    return {
      job_number:                  record.jobNumber,
      affidavit_sign_status:       record.signStatus,
      affidavit_sign_key:          record.signKey,
      affidavit_sign_iv:           record.signIv,
      affidavit_sign_platform:     record.platform,
      affidavit_sign_document:     record.document,
      affidavit_sign_document_format: record.documentFormat,
      affidavit_sign_datetime:     record.signedAt,
      affidavit_rejected_comments: record.rejectedComments,
      affidavit_rejected_datetime: record.rejectedAt,
    };
  }
}
